/**
 * web/src/games/WhereAmIPanel.tsx — Where Am I, the guess-the-place round
 * (card gzUOpwYknx7V, child dtdO7kPVQR5t, notebook save child u6kDmxyFbShW).
 *
 * The panel renders state and never decides anything: the clue ladder, whether
 * a guess identified the place, and what a retire means are all the server's
 * answers, re-read after every action. Both partners play the SAME round, so
 * there is one clue list and one shared guess feed — the guess feed is the
 * point of the panel, because it is how the other person sees what has already
 * been tried.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { WhereAmIRound } from '../../../shared/types';
import { api, session } from '../shell/bridge';
import { Pressable } from '../ui/Pressable';

const BASE = '/api/game/where-am-i';

interface Member {
  memberId: string;
  displayName: string;
}

/** The wire throws `Error(JSON.stringify({error:{code}}))`; the panel reacts to
 *  the code, never to the prose. */
function errorCode(e: unknown): string | null {
  if (!(e instanceof Error)) return null;
  try {
    return (JSON.parse(e.message) as { error?: { code?: string } })?.error?.code ?? null;
  } catch {
    return null;
  }
}

const ERROR_COPY: Record<string, string> = {
  not_open: 'That round has closed — start a new one.',
  not_found: 'That round is gone. Start a new one.',
  plus_required: 'Where Am I is part of Pear Plus.',
};

export function WhereAmIPanel({ members }: { members: Member[] }) {
  const me = session();
  const [round, setRound] = useState<WhereAmIRound | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /* The save is server-side idempotent (the round id is the notebook's source
     event), so the panel needs no local memory of it: `round.filed` is the
     truth, re-read after the POST. */
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const data = await api<{ round: WhereAmIRound | null }>(`${BASE}/round`);
      if (mine !== gen.current) return;
      setRound(data.round ?? null);
      setLoadError(false);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] where-am-i load failed', e);
        if (mine === gen.current) setLoadError(true);
      }
    } finally {
      if (mine === gen.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* A reveal in the other partner's client must land here too, and a tab that
     was asleep must not show a round the couple already finished. */
  useEffect(() => {
    const onWhere = () => void reload();
    const onResume = () => void reload();
    window.addEventListener('pear:whereami', onWhere);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('pear:reconnected', onResume);
    document.addEventListener('visibilitychange', onResume);
    return () => {
      window.removeEventListener('pear:whereami', onWhere);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pear:reconnected', onResume);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, [reload]);

  const act = async (path: string, body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const data = await api<{ round: WhereAmIRound }>(`${BASE}${path}`, { body });
      setRound(data.round);
      setError(null);
    } catch (e) {
      setError(ERROR_COPY[errorCode(e) ?? ''] ?? 'That did not save — try again.');
      console.error('[Pear] where-am-i action failed', e);
    } finally {
      setBusy(false);
    }
  };

  const start = () => void act('/start', {});
  const revealClue = () => round && void act('/clue', { roundId: round.roundId });
  const retire = () => round && void act('/retire', { roundId: round.roundId });
  const answer = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!round || !text || busy) return;
    setDraft('');
    void act('/answer', { roundId: round.roundId, text });
  };

  /* Save to notebook (child u6kDmxyFbShW): the round's own route, which files
     one idea (Ideas collection, not Memories — Jonathan 2026-11-04) through the
     existing notebook path. The round id is the source event, so a second tap —
     or both partners tapping at once — lands on the idea already written rather
     than a second copy. */
  const save = async () => {
    if (!round || round.filed || busy) return;
    await act('/save', { roundId: round.roundId });
  };

  const nameOf = (memberId: string) =>
    memberId === me?.memberId
      ? 'You'
      : members.find(m => m.memberId === memberId)?.displayName ?? 'Your partner';
  const closed = round ? round.state !== 'open' : false;

  return (
    <section className="game-panel whereami-panel" id="whereami-panel" aria-labelledby="whereami-title">
      <h3 className="game-title" id="whereami-title">Where am I</h3>

      {!loaded && !loadError && <p className="game-hint">Loading…</p>}

      {loaded && loadError && !round && (
        <div className="game-actions">
          <p className="game-error" id="whereami-loaderror">
            Could not load the game — the connection may have dropped.
          </p>
          <Pressable className="pear-btn pear-btn-mustard" id="whereami-retry" disabled={busy} onClick={() => void reload()}>
            Try again
          </Pressable>
        </div>
      )}

      {loaded && !loadError && !round && (
        <>
          <p className="game-hint" id="whereami-empty">
            One of us picks a famous place. The clues start obscure and get easier — guess it together,
            or retire the round and see where it was.
          </p>
          <Pressable className="pear-btn pear-btn-mustard" id="whereami-start" disabled={busy} onClick={start}>
            Start a round
          </Pressable>
        </>
      )}

      {loaded && round && (
        <>
          <ol className="whereami-clues" id="whereami-clues" aria-label="Clues so far">
            {round.clues.map((clue, i) => (
              <li key={`${round.roundId}-${i}`} className="whereami-clue" data-step={i + 1}>
                {clue}
              </li>
            ))}
          </ol>

          {!closed && (
            <>
              <form className="game-form" id="whereami-form" onSubmit={answer}>
                <label className="game-hint" htmlFor="whereami-guess">Where is it?</label>
                <input
                  className="game-input"
                  id="whereami-guess"
                  value={draft}
                  autoComplete="off"
                  placeholder="Type a place"
                  onChange={e => setDraft(e.target.value)}
                />
                {/* A real submit button, so Enter in the field and the tap are
                    the same action. */}
                <Pressable
                  className="pear-btn pear-btn-mustard"
                  id="whereami-answer"
                  type="submit"
                  disabled={busy || !draft.trim()}
                >
                  Answer
                </Pressable>
              </form>

              <div className="game-actions">
                {round.canRevealClue && (
                  <button type="button" className="pear-btn" id="whereami-clue" disabled={busy} onClick={revealClue}>
                    Another clue
                  </button>
                )}
                <button type="button" className="pear-btn" id="whereami-retire" disabled={busy} onClick={retire}>
                  Retire
                </button>
              </div>
            </>
          )}

          {closed && (
            <div className="whereami-reveal" id="whereami-reveal" data-state={round.state}>
              <p className="game-status" id="whereami-place">
                {round.state === 'identified' ? 'You got it — ' : 'It was '}
                <strong>{round.place}</strong>
                {round.region ? ` · ${round.region}` : ''}
              </p>
              <div className="game-actions">
                {round.state === 'identified' && (
                  <button
                    type="button"
                    className="pear-btn"
                    id="whereami-save"
                    disabled={busy || round.filed}
                    onClick={() => void save()}
                  >
                    {round.filed ? 'Saved to notebook' : 'Save to notebook'}
                  </button>
                )}
                <button type="button" className="pear-btn pear-btn-blush" id="whereami-next" disabled={busy} onClick={start}>
                  Play another
                </button>
              </div>
            </div>
          )}

          {round.guesses.length > 0 && (
            <ul className="gm-history" id="whereami-guesses" aria-label="Guesses so far">
              {round.guesses.slice().reverse().map((g, i) => (
                <li key={`${round.roundId}-g-${g.at}-${i}`} className="gm-history-row" data-hit={String(g.hit)}>
                  <span className="gm-history-q">{g.text}</span>
                  <span className="gm-history-v">{nameOf(g.memberId)}{g.hit ? ' · got it' : ''}</span>
                </li>
              ))}
            </ul>
          )}

          {error && (
            <p className="game-error" role="alert" id="whereami-error">{error}</p>
          )}
        </>
      )}
    </section>
  );
}
